import { Navigate, useOutletContext, useSearchParams } from "react-router-dom";
import type * as type from "@vTypes";
import type {
  IssuedAssignmentsViewBundle,
  Roster,
  ShiftManage,
} from "@vTypes/snapshot-type.ts";
import type { UserData } from "@/types/user-type";
import RosterViewAllTabContent from "@/pages/roster-view/components/RosterViewAllTabContent";
import RosterViewMyTabContent from "@/pages/roster-view/components/RosterViewMyTabContent";
import type { ShiftLookupMaps } from "@/utils/shift-util";
import type { ShiftCatalogShift } from "@/services/shift/shiftCatalog";
export type RosterViewOutletContext = {
  loginUser: UserData | null;
  issuedList: { year: number; month: number; schedule_id: string }[];
  currentScheduleDate: { year: number; month: number; schedule_id: string };
  setCurrentScheduleDate: React.Dispatch<
    React.SetStateAction<
      { year: number; month: number; schedule_id: string } | undefined
    >
  >;
  holidayDates: string[];
  selectedGroupId: string | null;
  /** 발행 스냅샷 `shifts` 카탈로그(시퀀스·띠 정렬 기준) */
  availableShifts: type.availableShifts[];
  shiftLookupMaps: ShiftLookupMaps<ShiftCatalogShift>;
  rosterSheet: Roster | null;
  monthlySchedule: unknown[] | null;
  shiftManager: ShiftManage | ShiftManage[];
  issuedDate: string | undefined;
  scheduleId: string | undefined;
  issuedMemo: string;
  /** 선택한 연·월 스냅샷 fetch 중(전역 스피너 대신 탭 영역 인디케이터용) */
  isRosterSnapshotLoading: boolean;
  /**
   * 발행 스냅샷 루트 `assignments` + 로그인 사용자 현재 병동(`UserData`).
   * 정렬된 월과 일치할 때만 채움.
   */
  issuedAssignmentsView: IssuedAssignmentsViewBundle | null;
  /** 발행 스냅샷 조회에 쓰는 `group_id`(URL 또는 로그인 소속) */
  issuedSnapshotGroupId: string;
  setIssuedSnapshotGroupId: (rawGroupId: string) => void;
};

export function RosterViewLegacyAllRedirect() {
  const [searchParams] = useSearchParams();
  const q = searchParams.toString();
  return <Navigate to={`/roster_view${q ? `?${q}` : ""}`} replace />;
}

export function RosterViewAllTab() {
  const ctx = useOutletContext<RosterViewOutletContext>();

  return (
    <RosterViewAllTabContent
      shiftManager={ctx.shiftManager}
      issuedList={ctx.issuedList}
      currentScheduleDate={ctx.currentScheduleDate}
      setCurrentScheduleDate={ctx.setCurrentScheduleDate}
      holidayDates={ctx.holidayDates}
      selectedGroupId={ctx.selectedGroupId}
      availableShifts={ctx.availableShifts}
      shiftLookupMaps={ctx.shiftLookupMaps}
      rosterSheet={ctx.rosterSheet}
      monthlySchedule={ctx.monthlySchedule}
      issuedMemo={ctx.issuedMemo}
      isRosterSnapshotLoading={ctx.isRosterSnapshotLoading}
      issuedDate={ctx.issuedDate != null ? new Date(ctx.issuedDate) : undefined}
      loginUser={ctx.loginUser}
      issuedAssignmentsView={ctx.issuedAssignmentsView}
      issuedSnapshotGroupId={ctx.issuedSnapshotGroupId}
      setIssuedSnapshotGroupId={ctx.setIssuedSnapshotGroupId}
    />
  );
}

export function RosterViewMyTab() {
  const ctx = useOutletContext<RosterViewOutletContext>();
  const [searchParams] = useSearchParams();
  const homeGroupId = (
    ctx.loginUser?.original_group_id ??
    ctx.loginUser?.group_id ??
    ""
  ).trim();
  const selectedGroupId = (ctx.selectedGroupId ?? "").trim();
  const shouldRedirectToAllTab =
    ctx.loginUser?.hn_auth === "HN" &&
    homeGroupId !== "" &&
    selectedGroupId !== "" &&
    homeGroupId !== selectedGroupId;

  if (shouldRedirectToAllTab) {
    const q = searchParams.toString();
    return <Navigate to={`/roster_view${q ? `?${q}` : ""}`} replace />;
  }

  return (
    <RosterViewMyTabContent
      issuedList={ctx.issuedList}
      holidayDates={ctx.holidayDates}
      currentScheduleDate={ctx.currentScheduleDate}
      setCurrentScheduleDate={(v) => ctx.setCurrentScheduleDate(v)}
      loginUser={ctx.loginUser as type.LoginUser | null}
      issuedDate={ctx.issuedDate != null ? new Date(ctx.issuedDate) : undefined}
      scheduleId={ctx.scheduleId ?? ""}
      availableShifts={ctx.availableShifts}
    />
  );
}
